import MarkdownIt from 'markdown-it';
import katex from 'katex';
import mermaid from 'mermaid';
import 'katex/dist/katex.min.css';
import markdownItKatex from '@kangc/v-md-editor/lib/utils/markdown-it-katex';
import markdownItDocMermaid from './markdown-it-doc-mermaid';
import markdownItDocVideo from './markdown-it-doc-video';
import markdownItImageSize from './markdown-it-image-size';
import {cleanHeadingText, getMarkdownInlinePlainText, slugify} from './document-helpers';

const MERMAID_CLASS = 'doc-mermaid mermaid';
let mdInstance = null;
let mermaidInitialized = false;

function ensureMermaid() {
  if (!mermaidInitialized) {
    mermaid.initialize({
      startOnLoad: false,
      theme: 'default',
      securityLevel: 'loose',
    });
    mermaidInitialized = true;
  }
  return mermaid;
}

/**
 * 为已有 markdown-it 实例增加流程图（及可选公式）
 * @param {object} md markdown-it 实例
 * @param {{ withKatex?: boolean }} opts mavon 已内置 katex 插件时传 withKatex: false
 */
export function extendMarkdownItForDocs(md, opts) {
  if (!md) return;
  const withKatex = !opts || opts.withKatex !== false;
  if (!md.__docImageSizeExtended) {
    md.use(markdownItImageSize);
    md.__docImageSizeExtended = true;
  }
  if (!md.__docVideoExtended) {
    md.use(markdownItDocVideo);
    md.__docVideoExtended = true;
  }
  if (md.__docMermaidExtended) return;
  if (withKatex && !md.__docKatexExtended) {
    md.use(markdownItKatex, {
      katex,
      throwOnError: false,
    });
    md.__docKatexExtended = true;
  }
  md.use(markdownItDocMermaid, {
    className: MERMAID_CLASS,
  });
  md.__docMermaidExtended = true;
}

function getMarkdownIt() {
  if (!mdInstance) {
    mdInstance = new MarkdownIt({
      html: true,
      linkify: true,
      breaks: true,
    });
    extendMarkdownItForDocs(mdInstance);
  }
  return mdInstance;
}

export function renderDocumentMarkdown(source) {
  if (!source) return '';
  const slugCount = {};
  const md = getMarkdownIt();
  md.renderer.rules.heading_open = (tokens, idx, options, env, self) => {
    const token = tokens[idx + 1];
    const raw = token && token.type === 'inline'
      ? getMarkdownInlinePlainText(token)
      : '';
    const text = cleanHeadingText(raw);
    let id = slugify(text);
    if (slugCount[id]) {
      slugCount[id] += 1;
      id = `${id}-${slugCount[id]}`;
    } else {
      slugCount[id] = 1;
    }
    tokens[idx].attrSet('id', id);
    return self.renderToken(tokens, idx, options);
  };
  return md.render(source);
}

/** 兼容被高亮成 code.language-mermaid 的旧 HTML */
function upgradeMermaidPres(root) {
  if (!root) return;
  root.querySelectorAll('pre code.language-mermaid').forEach((codeEl) => {
    const pre = codeEl.parentNode;
    if (!pre || pre.classList.contains('doc-mermaid')) return;
    const block = document.createElement('pre');
    block.className = MERMAID_CLASS;
    block.textContent = codeEl.textContent || '';
    pre.parentNode.replaceChild(block, pre);
  });
}

/** 将 ```mermaid 代码块渲染为 SVG */
export function initDocumentMermaid(root) {
  if (!root || typeof window === 'undefined') return;
  upgradeMermaidPres(root);
  const pending = root.querySelectorAll('.doc-mermaid.mermaid');
  const eles = Array.from(pending).filter((el) => !el.querySelector('svg'));
  if (!eles.length) return;
  try {
    const api = ensureMermaid();
    if (typeof api.init !== 'function') {
      console.warn('[document-markdown] mermaid.init unavailable');
      return;
    }
    api.init(null, eles);
    eles.forEach((el) => el.setAttribute('data-processed', '1'));
  } catch (err) {
    console.warn('[document-markdown] mermaid render failed', err);
  }
}
